import type { ReactElement } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  EXTENSION_SCAN_RESULT_FROM_ALERTS,
  EXTENSION_SCAN_RESULT_RETURN_TO_PARAM,
  isSafeScanResultReturnTo,
} from '@/components/grouped-alerts/extension-scan-result-link';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { CATALOG_TIERS } from '@/components/policylist/constants/builtin-catalog';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useExtensionScanResult } from '@/hooks/api/useExtensions';
import { formatAlertType } from '@kirin/api-client/helpers/alerts';
import type { ExtensionCapability } from '@kirin/api-client/contracts/analyses';
import type { ExtensionScanResultFinding } from '@kirin/api-client/contracts/extensions';

interface ExtensionScanResultPageProps {
  organizationId?: string;
}

interface ScanResultBackNav {
  path: string;
  crumbLabel: string;
  backLabel: string;
}

/** Short category labels for the scan-result capability list (mock). */
const CAPABILITY_CATEGORY_LABELS: Record<string, string> = {
  'ai-ide': 'AI & IDE',
  activation: 'Activation',
  'supply-chain': 'Supply chain',
  runtime: 'Runtime',
  notebook: 'Notebook',
  custom: 'Custom',
  'credential-access': 'Credentials',
  'credential-targeting': 'Credentials',
  debugger: 'Debugger',
  'code-execution': 'Code execution',
  'custom-editor': 'Custom editor',
  filesystem: 'File system',
  'capability-registration': 'Capabilities',
};

/** Prefer short titles when we know the catalog rule id. */
const CAPABILITY_TITLES_BY_RULE_ID: Record<string, string> = {
  catalog_mcp_server_provider: 'MCP server registration',
  catalog_language_model_tools: 'LLM tool registration',
  catalog_chat_participant: 'Chat participant',
  catalog_activation_uri: 'Deep-link activation',
  catalog_extension_pack: 'Extension chaining',
  catalog_extension_dependencies: 'Extension chaining',
  catalog_tasks: 'Task definitions',
  catalog_terminal: 'Terminal profile',
  catalog_no_repository: 'Missing repository',
  catalog_debugger: 'Debug adapter',
  catalog_custom_editor: 'Custom editor',
  catalog_filesystem_providers: 'Virtual filesystem',
};

function resolveScanResultBackNav(
  organizationId: string | undefined,
  from: string | null,
  returnTo: string | null
): ScanResultBackNav {
  if (from === EXTENSION_SCAN_RESULT_FROM_ALERTS) {
    const alertsFallback = organizationId
      ? `/${organizationId}/alerts`
      : '/dashboard/alerts';
    return {
      path: isSafeScanResultReturnTo(returnTo) ? returnTo : alertsFallback,
      crumbLabel: 'Alerts',
      backLabel: 'Back to Alerts',
    };
  }
  return {
    path: organizationId
      ? `/${organizationId}/inventory?tab=extensions`
      : '/dashboard/inventory?tab=extensions',
    crumbLabel: 'Inventory',
    backLabel: 'Back to Inventory',
  };
}

function capabilityTitle(capability: ExtensionCapability): string {
  const known = CAPABILITY_TITLES_BY_RULE_ID[capability.ruleId];
  if (known) return known;
  const firstClause = capability.description.split(/[.—]/)[0]?.trim();
  return firstClause || capability.field;
}

function capabilityCategoryLabel(category: string): string {
  return CAPABILITY_CATEGORY_LABELS[category] ?? category;
}

const CATALOG_DESCRIPTION_BY_RULE_ID = new Map(
  CATALOG_TIERS.flatMap(tier =>
    tier.rules.map(rule => [rule.id, rule.description] as const)
  )
);

const CATALOG_DESCRIPTION_BY_FIELD = new Map(
  CATALOG_TIERS.flatMap(tier =>
    tier.rules.map(rule => [rule.field, rule.description] as const)
  )
);

function catalogDescriptionForContributor(
  ruleId: string,
  field: string
): string | undefined {
  return (
    CATALOG_DESCRIPTION_BY_RULE_ID.get(ruleId) ??
    CATALOG_DESCRIPTION_BY_FIELD.get(field)
  );
}

const FIELD_BADGE_CLASS =
  'inline-flex cursor-help items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300';

function FieldBadgeWithTooltip({
  field,
  description,
}: {
  field: string;
  description?: string;
}): ReactElement {
  const badge = <code className={FIELD_BADGE_CLASS}>{field}</code>;
  if (!description) {
    return badge;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <p>{description}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function FindingItem({
  finding,
}: {
  finding: ExtensionScanResultFinding;
}): ReactElement {
  return (
    <div className="py-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold leading-none text-foreground">
          {formatAlertType(finding.type)}
        </h3>
        <SeverityBadge
          riskLevel={finding.severity}
          className="h-6 shrink-0 px-2.5 py-0 text-xs"
        />
      </div>

      <div className="mt-2.5 flex flex-col gap-2">
        {finding.score !== undefined && (
          <Badge
            variant="outline"
            className="w-fit rounded-full border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300"
          >
            Score {finding.score}
          </Badge>
        )}

        {finding.scoreContributors && finding.scoreContributors.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {finding.scoreContributors.map(contributor => (
              <FieldBadgeWithTooltip
                key={`${contributor.ruleId}-${contributor.field}`}
                field={contributor.field}
                description={catalogDescriptionForContributor(
                  contributor.ruleId,
                  contributor.field
                )}
              />
            ))}
          </div>
        )}
      </div>

      <p className="mt-3 text-sm leading-relaxed text-foreground/90">
        {finding.reason}
      </p>

      <Badge
        variant="outline"
        className="mt-3 w-fit rounded-full border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300"
      >
        Alert created: {finding.type}
      </Badge>
    </div>
  );
}

function CapabilityRow({
  capability,
}: {
  capability: ExtensionCapability;
}): ReactElement {
  const description =
    capability.description ||
    catalogDescriptionForContributor(capability.ruleId, capability.field);

  return (
    <div className="border-b border-border/60 py-3 last:border-b-0">
      <div className="flex items-center gap-2">
        <Badge
          variant="secondary"
          className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300"
        >
          {capabilityCategoryLabel(capability.category)}
        </Badge>
        <p className="min-w-0 flex-1 text-sm font-semibold text-foreground">
          {capabilityTitle(capability)}
        </p>
        <SeverityBadge
          riskLevel={capability.severity}
          className="h-6 shrink-0 px-2.5 py-0 text-xs"
        />
      </div>
      <div className="mt-1.5">
        <FieldBadgeWithTooltip
          field={capability.field}
          description={description}
        />
      </div>
    </div>
  );
}

export function ExtensionScanResultPage({
  organizationId,
}: ExtensionScanResultPageProps): ReactElement {
  const { extensionId } = useParams<{ extensionId: string }>();
  const [searchParams] = useSearchParams();
  const version = searchParams.get('version') ?? undefined;
  const backNav = resolveScanResultBackNav(
    organizationId,
    searchParams.get('from'),
    searchParams.get(EXTENSION_SCAN_RESULT_RETURN_TO_PARAM)
  );
  const { data, isLoading, isError } = useExtensionScanResult(
    organizationId,
    extensionId,
    version
  );

  if (isLoading) {
    return (
      <ScanResultFrame backNav={backNav}>
        <p className="text-muted-foreground">Loading extension scan result…</p>
      </ScanResultFrame>
    );
  }

  if (isError || !data) {
    return (
      <ScanResultFrame backNav={backNav}>
        <p className="text-destructive">
          Extension scan result could not be loaded.
        </p>
      </ScanResultFrame>
    );
  }

  const versionLabel = data.version
    ? `v${data.version}`
    : 'version unavailable';

  return (
    <ScanResultFrame backNav={backNav}>
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">Scan result</h1>
          <Badge
            variant="secondary"
            className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            {data.extensionId}
          </Badge>
          <p className="text-sm text-muted-foreground">
            {versionLabel} · publisher: {data.publisher}
            {!data.isVerified && (
              <span className="text-destructive"> (not trusted)</span>
            )}
          </p>
        </div>
        <p className="text-sm text-muted-foreground">
          Security findings and detected capabilities are shown separately.{' '}
          <span className="font-semibold text-foreground">
            Capabilities are informational and are not counted as security
            issues.
          </span>
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="overflow-hidden rounded-xl border bg-background">
          <div className="flex items-center gap-2 border-b border-red-100 bg-red-50/80 px-4 py-3 dark:border-red-950 dark:bg-red-950/30">
            <h2 className="text-sm font-semibold">Security Findings</h2>
            <Badge
              variant="secondary"
              className="ml-auto bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-100"
            >
              {data.findings.length}
            </Badge>
          </div>
          <div className="px-4 pt-3 pb-3">
            <p className="text-xs text-muted-foreground">
              Counted in risk level. Can start alert workflows.
            </p>
          </div>
          <div className="divide-y divide-border border-t border-border px-4">
            {data.findings.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">
                No security findings for this scan.
              </p>
            ) : (
              data.findings.map((finding, index) => (
                <FindingItem
                  key={`${finding.type}-${index}`}
                  finding={finding}
                />
              ))
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-xl border bg-background">
          <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
            <h2 className="text-sm font-semibold">Detected Capabilities</h2>
            <Badge variant="secondary" className="ml-auto">
              {data.capabilities.length}
            </Badge>
          </div>
          <div className="px-4 pt-3 pb-3">
            <p className="text-xs text-muted-foreground">
              Informational. Not counted as security issues. Does not raise the
              risk level.
            </p>
          </div>
          <div className="border-t border-border px-4 pb-1">
            {data.capabilities.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">
                No capabilities detected for this scan.
              </p>
            ) : (
              data.capabilities.map((capability, index) => (
                <CapabilityRow
                  key={`${capability.ruleId}-${index}`}
                  capability={capability}
                />
              ))
            )}
          </div>
        </section>
      </div>
    </ScanResultFrame>
  );
}

function ScanResultFrame({
  backNav,
  children,
}: {
  backNav: ScanResultBackNav;
  children: ReactElement | ReactElement[];
}): ReactElement {
  return (
    <div className="flex flex-col gap-4">
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-2 text-muted-foreground text-sm"
      >
        <Link to={backNav.path} className="hover:text-foreground">
          {backNav.crumbLabel}
        </Link>
        <span aria-hidden>›</span>
        <span className="text-foreground">Extension scan result</span>
      </nav>
      <Link
        to={backNav.path}
        className="inline-flex w-fit items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {backNav.backLabel}
      </Link>
      <div className="rounded-xl border bg-background p-6 shadow-sm">
        <div className="flex flex-col gap-6">{children}</div>
      </div>
    </div>
  );
}

ExtensionScanResultPage.displayName = 'ExtensionScanResultPage';
